<script setup>
import { ref, computed } from "vue";
import { hasAnyPermission, COLLABORATOR_PERMISSIONS } from "../../composables/usePermissions.js";
import { downloadBase64File } from "../../composables/useFileDownload.js";
import { getFileTypeLabel } from "../../composables/useFileType.js";
import ProjectService from "../../services/projectService.js";

const props = defineProps({
  // { id, name, createdAt, sourceSize, origin/originMimeType, uploaderName, uploadedBy }
  document: { type: Object, required: true },
  // Optional — used to resolve the uploader's role and gate the Download
  // button on the current user's own permissions. Without it the uploader
  // role badge is omitted and downloading is assumed allowed.
  project: { type: Object, default: null },
});
const emit = defineEmits(["close"]);

const projectService = new ProjectService();

function close() { emit("close"); }

const ROLE_LABELS = { OWNER: "Owner", EDITOR: "Editor", CONTRIBUTOR: "Contributor", VIEWER: "Viewer" };

const uploader = computed(() => {
  const email = props.document.uploadedBy;
  const owner = props.project?.ownerProfile;
  if (owner && email && owner.email === email) {
    return { name: owner.name, email: owner.email, role: "OWNER" };
  }
  const collab = (props.project?.topCollaborators ?? []).find((c) => c.user?.email === email);
  if (collab) return { name: collab.user?.name, email: collab.user?.email, role: collab.collaboratorRole };
  return { name: props.document.uploaderName, email, role: null };
});

function initials(person) {
  const label = person?.name || person?.email || "?";
  return label.slice(0, 2).toUpperCase();
}

const fileTypeLabel = computed(() => getFileTypeLabel(props.document.origin?.mimeType ?? props.document.originMimeType, props.document.name));

const canDownload = computed(() =>
  !props.project
  || hasAnyPermission(props.project.permissions, [COLLABORATOR_PERMISSIONS.DOWNLOAD_SOURCE_DOCUMENT, COLLABORATOR_PERMISSIONS.DOWNLOAD_ORIGINAL_DOCUMENT])
);

const isDownloading = ref(false);
async function onDownload() {
  if (isDownloading.value) return;
  isDownloading.value = true;
  try {
    const { content, originMimeType } = await projectService.downloadDocument({
      documentId: props.document.id,
      flatten: false,
      original: true,
    });
    if (content) downloadBase64File(content, originMimeType, props.document.name ?? "document");
  } finally {
    isDownloading.value = false;
  }
}

function formatDetailDate(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatFileSize(bytes) {
  if (!bytes) return "—";
  if (bytes <= 1_000_000) return `${(bytes / 1000).toFixed(1)} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}
</script>

<template>
  <!-- Teleported to <body> — one of its two call sites renders this from
       inside DocumentCardMenu.vue's wrapper, which can sit in a table cell
       with text-right (used to align the "..." trigger); un-teleported, that
       inherited alignment bleeds into this modal's own text. -->
  <Teleport to="body">
    <div class="fixed inset-0 bg-black/20 dark:bg-black/50 z-[400] flex items-center justify-center p-4" @click.self="close">
      <div class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[480px] overflow-hidden text-left">

      <!-- Header -->
      <div class="bg-slate-700 dark:bg-slate-800 text-white p-6 flex items-start gap-4">
        <div class="flex items-center justify-center w-14 h-14 rounded-xl bg-slate-600/60 shrink-0">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8L14 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M14 2v6h6M8 13h8M8 17h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
        </div>
        <div class="flex-1 min-w-0">
          <h2 class="text-xl font-bold m-0 truncate">{{ document.name ?? "Untitled" }}</h2>
          <p class="text-sm text-slate-300 m-0 mt-0.5">Preview not available</p>
        </div>
        <button
          class="flex items-center justify-center w-8 h-8 rounded-lg text-slate-300 hover:bg-white/10 hover:text-white border-0 bg-transparent cursor-pointer transition-colors shrink-0"
          @click="close"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <div class="p-6">
        <!-- Info banner -->
        <div class="flex items-start gap-3 mb-6">
          <svg class="text-slate-400 shrink-0 mt-0.5" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
            <path d="M12 11v5M12 8h.01" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          </svg>
          <p class="text-sm text-slate-600 dark:text-slate-300 m-0 leading-relaxed">
            This file type is not supported for preview or annotation. You can download the file to view it locally.
          </p>
        </div>

        <!-- Details -->
        <h3 class="text-sm font-bold text-slate-700 dark:text-slate-200 m-0 mb-2">Details</h3>
        <div class="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 grid grid-cols-3 gap-4 mb-6">
          <div>
            <p class="text-xs text-slate-400 dark:text-slate-500 m-0 mb-1">Date uploaded</p>
            <p class="text-sm font-semibold text-slate-800 dark:text-slate-100 m-0">{{ formatDetailDate(document.createdAt) }}</p>
          </div>
          <div>
            <p class="text-xs text-slate-400 dark:text-slate-500 m-0 mb-1">File type</p>
            <p class="text-sm font-semibold text-slate-800 dark:text-slate-100 m-0">{{ fileTypeLabel }}</p>
          </div>
          <div>
            <p class="text-xs text-slate-400 dark:text-slate-500 m-0 mb-1">Size</p>
            <p class="text-sm font-semibold text-slate-800 dark:text-slate-100 m-0">{{ formatFileSize(document.sourceSize) }}</p>
          </div>
        </div>

        <!-- Uploaded by -->
        <template v-if="uploader.name || uploader.email">
          <h3 class="text-sm font-bold text-slate-700 dark:text-slate-200 m-0 mb-2">Uploaded by</h3>
          <div class="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 flex items-center gap-3 mb-2">
            <div class="flex items-center justify-center w-11 h-11 rounded-full bg-[#7ba7d4] text-white text-sm font-semibold shrink-0">
              {{ initials(uploader) }}
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-slate-800 dark:text-slate-100 m-0 truncate">{{ uploader.name || uploader.email }}</p>
              <p v-if="uploader.name && uploader.email" class="text-[13px] text-slate-400 dark:text-slate-500 m-0 truncate">{{ uploader.email }}</p>
            </div>
            <span
              v-if="uploader.role"
              class="shrink-0 px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 text-sm font-medium"
            >
              {{ ROLE_LABELS[uploader.role] ?? uploader.role }}
            </span>
          </div>
        </template>
      </div>

      <div class="h-px bg-slate-200 dark:bg-slate-700" />

      <!-- Footer -->
      <div class="p-6 flex items-center justify-end">
        <p v-if="!canDownload" class="text-sm text-slate-400 dark:text-slate-500 m-0">You don't have permission to download this file</p>
        <button
          v-else
          :disabled="isDownloading"
          class="flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-800 dark:bg-slate-700 text-white text-sm font-semibold hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors cursor-pointer disabled:opacity-50"
          @click="onDownload"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {{ isDownloading ? "Downloading…" : "Download" }}
        </button>
      </div>
      </div>
    </div>
  </Teleport>
</template>
